import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Logo from "@/components/Logo";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import PasswordInput from "@/components/PasswordInput";
import AuthPromo from "@/components/AuthPromo";
import { toast } from "sonner";
import { ArrowRight, Loader2, ShieldCheck, KeyRound, ArrowLeft } from "lucide-react";

const AUTH_BG = "https://static.prod-images.emergentagent.com/jobs/06b5cb7e-c888-4c81-b88c-5244c1d0af32/images/fd1c566f5b8864f6832e4d35cf9140f3939f77c249aa2aa7f7d74ab738a59602.png";

export default function Login() {
  const [stage, setStage] = useState("creds");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [tempToken, setTempToken] = useState("");
  const [method, setMethod] = useState("email");
  const [code, setCode] = useState("");
  const [devOtp, setDevOtp] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { refreshUser, setUser } = useAuth();

  const submitCreds = async (e) => {
    e?.preventDefault?.();
    if (!email || !password) { toast.error("Enter your email and password."); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email: email.trim(), password });
      if (data.requires_2fa) {
        setTempToken(data.temp_token);
        setMethod(data.method);
        setDevOtp(data.dev_otp || null);
        if (data.dev_otp) toast.warning(`Demo code: ${data.dev_otp}`);
        else if (data.method === "email") toast.success("Code sent to your email.");
        setStage("verify");
      } else {
        // Admin path — server returned an access token, no 2FA needed.
        // Use the user payload from the login response directly to avoid a
        // race where AppLayout re-renders before refreshUser() commits state
        // and bounces back to /login.
        const u = data.user || (await refreshUser());
        if (u) setUser(u);
        toast.success("Welcome back");
        navigate(u?.role === "admin" ? "/admin" : "/app", { replace: true });
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setLoading(false); }
  };

  const verifyCode = async () => {
    if (code.length < 4) { toast.error("Enter the 6-digit code."); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login/verify", { temp_token: tempToken, code });
      // Prefer the fresh user payload from the response — avoids the race
      // where AppLayout reads a stale `user === false` state and redirects
      // back to /login before refreshUser() finishes committing.
      const u = data?.user || (await refreshUser());
      if (u) setUser(u);
      toast.success("Welcome back");
      navigate(u?.role === "admin" ? "/admin" : "/app", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setLoading(false); }
  };

  const resendOtp = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login/resend-otp", { temp_token: tempToken });
      setDevOtp(data.dev_otp || null);
      if (data.dev_otp) toast.warning(`Demo code: ${data.dev_otp}`);
      else toast.success("New code sent.");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="bg-[#F0ECE0] flex flex-col">
        <div className="px-8 md:px-14 pt-8 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="login-logo">
            <Logo variant="wordmark" size={36} />
          </Link>
          <Link to="/register" className="text-sm text-[#4F4D45] hover:text-[#1B3526]" data-testid="login-link-register">New here? Open account</Link>
        </div>
        <div className="flex-1 px-8 md:px-14 pt-16 pb-10 flex flex-col">
          {stage === "creds" && (
            <form onSubmit={submitCreds} className="max-w-md w-full">
              <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Welcome back.</h2>
              <p className="text-[#4F4D45] mt-3">Sign in to your FinVault wallet.</p>
              <div className="mt-8 space-y-4">
                <div>
                  <Label className="text-eyebrow text-[#4F4D45]">Email</Label>
                  <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" data-testid="login-input-email" className="mt-1" />
                </div>
                <div>
                  <div className="flex items-center justify-between">
                    <Label className="text-eyebrow text-[#4F4D45]">Password</Label>
                    <Link to="/forgot-password" className="text-xs text-[#1B3526] hover:underline" data-testid="login-forgot-link">Forgot password?</Link>
                  </div>
                  <PasswordInput value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Your password" testId="login-input-password" className="mt-1" />
                </div>
              </div>
              <Button type="submit" disabled={loading} className="mt-8 bg-[#1B3526] hover:bg-[#0F2517] w-full sm:w-auto" data-testid="login-submit">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Continue <ArrowRight className="ml-2 w-4 h-4" /></>}
              </Button>
              <p className="text-xs text-[#4F4D45] mt-8 flex items-center gap-2"><ShieldCheck className="w-3.5 h-3.5 text-[#1B3526]" /> Every login is verified with a one-time code.</p>
            </form>
          )}

          {stage === "verify" && (
            <div className="max-w-md w-full">
              <div className="w-12 h-12 rounded-xl bg-[#1B3526]/10 text-[#1B3526] flex items-center justify-center mb-6">
                <KeyRound className="w-6 h-6" strokeWidth={1.5} />
              </div>
              <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight">
                {method === "totp" ? "Enter your authenticator code" : "Verify it's you"}
              </h2>
              <p className="text-[#4F4D45] mt-3">
                {method === "totp"
                  ? "Open your authenticator app and enter the 6-digit code for FinVault."
                  : <>We sent a 6-digit code to <strong className="text-[#0A0A08]">{email}</strong>.</>}
              </p>
              {devOtp && (
                <div className="mt-4 p-3 rounded-lg bg-[#B58957]/20 border border-[#B58957] text-sm" data-testid="login-dev-otp">
                  Demo code: <strong>{devOtp}</strong>
                </div>
              )}
              <div className="mt-8">
                <Label className="text-eyebrow text-[#4F4D45]">Code</Label>
                <Input value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="000000" inputMode="numeric" className="mt-1 text-2xl tracking-[0.4em] font-mono text-center" data-testid="login-input-code" />
              </div>
              <div className="mt-4 flex items-center justify-between">
                {method === "email" ? (
                  <button type="button" onClick={resendOtp} disabled={loading} className="text-sm text-[#1B3526] hover:underline" data-testid="login-resend-otp">Resend code</button>
                ) : <span />}
                <button onClick={() => setStage("creds")} className="text-sm text-[#4F4D45] hover:text-[#1B3526] flex items-center gap-1" data-testid="login-back-to-creds"><ArrowLeft className="w-3.5 h-3.5" /> back</button>
              </div>
              <Button onClick={verifyCode} disabled={loading} className="mt-8 bg-[#1B3526] hover:bg-[#0F2517] w-full sm:w-auto" data-testid="login-verify-button">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Sign in <ArrowRight className="ml-2 w-4 h-4" /></>}
              </Button>
            </div>
          )}
        </div>
      </div>
      <div className="hidden lg:block relative">
        <AuthPromo />
      </div>
    </div>
  );
}
